import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SelectItem, SelectSeparator } from "@/components/ui/select";
import {
  isProductArchived,
  partitionProductCatalogOptions,
} from "@/lib/productCatalogOptions";
import type { ProductCatalogOption } from "@/types/ProductCatalog";

export function ProductSelectItems({
  products,
  selectedProductId,
  getLabel,
}: {
  products: ProductCatalogOption[];
  selectedProductId?: string;
  getLabel?: (product: ProductCatalogOption) => string;
}) {
  const label = getLabel ?? ((product) => product.name?.trim() || "Untitled");
  const { active, archived } = useMemo(
    () => partitionProductCatalogOptions(products),
    [products],
  );
  const selectedIsArchived = useMemo(() => {
    if (!selectedProductId) return false;
    const selected = products.find((product) => product._id === selectedProductId);
    return selected ? isProductArchived({ product: selected }) : false;
  }, [products, selectedProductId]);
  const [archivedOpen, setArchivedOpen] = useState(selectedIsArchived);

  useEffect(() => {
    if (selectedIsArchived) {
      setArchivedOpen(true);
    }
  }, [selectedIsArchived]);

  return (
    <>
      {active.map((product) => (
        <SelectItem key={product._id} value={product._id}>
          {label(product)}
        </SelectItem>
      ))}
      {archived.length > 0 ? (
        <>
          <SelectSeparator />
          <button
            type="button"
            className="flex w-full items-center gap-1 rounded-sm px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setArchivedOpen((open) => !open);
            }}
          >
            {archivedOpen ? (
              <ChevronDown className="h-3 w-3 shrink-0" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0" />
            )}
            Archived ({archived.length})
          </button>
          {archivedOpen
            ? archived.map((product) => (
                <SelectItem key={product._id} value={product._id}>
                  {label(product)}
                </SelectItem>
              ))
            : null}
        </>
      ) : null}
    </>
  );
}
